import { useEffect, useLayoutEffect, useRef } from 'react'

import { Fade } from 'components/misc/Fade'
import useClickOut from 'hooks/useClickOut'
import styles from './styles/Dropdown.css'
import classNames from 'classnames'
import HoverLink from './HoverLink'

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export const Dropdown = ({
  open,
  setOpen,
  title,
  children,
  ariaLabel,
  className = undefined,
  showTriangle = true,
  animate = false,
  onTriggerClick
}) => {
  const ref = useRef(null)
  const panelRef = useRef(null)
  const Panel = animate ? 'div' : Fade

  useBrowserLayoutEffect(() => {
    const panel = panelRef.current
    if (!animate || !panel) return

    // Restore focus before making a closing panel inert, including hover-driven closes.
    if (!open && panel.contains(document.activeElement)) {
      ref.current.querySelector('[role="button"]')?.focus()
    }
    panel.inert = !open
    panel.setAttribute('aria-hidden', String(!open))
  }, [animate, open])

  useClickOut(ref, open, () => {
    setOpen(false)
  })

  useEffect(() => {
    if (!open) return

    const handleKeyDown = event => {
      if (event.key === 'Escape' && ref.current?.contains(event.target)) {
        setOpen(false)
        ref.current.querySelector('[role="button"]')?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, setOpen])

  return (
    <div
      ref={ref}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false)
      }}
    >
      <HoverLink
        open={open}
        onClick={onTriggerClick || (() => setOpen(!open))}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen(!open)
          }
        }}
        showTriangle={showTriangle}
        includeArrow
        ariaLabel={ariaLabel}
        expanded={open}
      >
        {title}
      </HoverLink>
      <Panel
        className={classNames(styles.dropdown, {
          [className]: className,
          [styles.animated]: animate,
          [styles.open]: animate && open
        })}
        {...(animate ? { ref: panelRef } : { show: open })}
      >
        <div>{children}</div>
      </Panel>
    </div>
  )
}

export default Dropdown
